import 'reflect-metadata';

import { injectable } from 'inversify';

import { Authority, User } from '@project44/ui-utils';

import { UnauthorizedAccessException } from '../../../shared/core/application/errors';
import { ParametersValueObject } from '../../../shared/core/domain/ParameterValueObject';
import { ValueObject } from '../../../shared/core/domain/ValueObject';

@injectable()
abstract class UseCase<T, K> {
  public abstract execute(
    user: User,
    parameters: ParametersValueObject<T>,
  ): Promise<ValueObject<K>>;

  protected validateAnyOfPermissions(
    user: User,
    permissions: Array<string>,
  ): void | never {
    if (!user.hasAnyOfPermissions(permissions as Array<Authority>)) {
      UnauthorizedAccessException.throw(permissions, user);
    }
  }
}

@injectable()
export abstract class QueryUseCase<T> extends UseCase<string, T> {}

@injectable()
export abstract class CommandUseCase<T, K> extends UseCase<T, K> {}

@injectable()
abstract class CQRSUseCase<T, K> {
  public abstract readonly requiredAnyOfPermissions: Array<string>;
  public abstract readonly requiredAllOfPermissions: Array<string>;

  public abstract execute(userPrincipal: User, payload: T): Promise<K>;

  public validateUseCase(userPrincipal: User): void | never {
    this.requiredAllOfPermissions.length > 0 &&
      this.validateAllOfPermissions(
        userPrincipal,
        this.requiredAllOfPermissions,
      );

    this.requiredAnyOfPermissions.length > 0 &&
      this.validateAnyOfPermissions(
        userPrincipal,
        this.requiredAnyOfPermissions,
      );
  }

  private validateAnyOfPermissions(
    user: User,
    permissions: Array<string>,
  ): void | never {
    if (!user.hasAnyOfPermissions(permissions as Array<Authority>)) {
      UnauthorizedAccessException.throw(permissions, user);
    }
  }

  private validateAllOfPermissions(
    user: User,
    permissions: Array<string>,
  ): void | never {
    if (!user.hasAllPermissions(permissions as Array<Authority>)) {
      UnauthorizedAccessException.throw(permissions, user);
    }
  }
}

@injectable()
export abstract class QueryHandler<T, K> extends CQRSUseCase<T, K> {}

@injectable()
export abstract class CommandHandler<T> extends CQRSUseCase<T, void> {}
